import type { PropsWithChildren } from 'react';
import React from 'react';
import billingUtils from '../utils/billingUtils';
import { EVENT_IDS } from '../model/ClickstreamEvents';
import { LoadingConstants } from '@zg-rentals/rental-manager-env';
import { ConfirmationModal, type ConfirmationModalProps } from '@zg-rentals/rental-revenue-components';
import { NOTIFICATION_TYPE } from '../constants/NotificationConstants';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  Icon,
  Label,
  Paragraph,
  Radio,
  Spinner,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import SpinnerContainer from './SpinnerContainer';
import BillingError from './BillingError';
import BillingAddPaymentModal from './modals/BillingAddPaymentModal';
import TermsAndConditionsWidget from './TermsAndConditionsWidget';

import { useNodeEnv, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useAppMaintenance } from '../hooks/useMaintenance';
import { useAccountPaymentMethods } from '../hooks/useAccountPaymentMethods';
import Cookies from 'js-cookie';
import { emitRemovePaymentMethodEvent } from '../model/ClickstreamEvents';
import type { UserZuoraPaymentMethodDto } from '@zg-rentals/ts-adproducts-client';

export const StyledAlertText = ({ children }: PropsWithChildren) => (
  <Box css={{ display: 'flex', alignItems: 'center' }}>{children}</Box>
);

const makeLabel = (isSinglePayment: boolean, isAutopayEnabled: boolean) => {
  if (isSinglePayment) {
    return 'Pay';
  }
  return isAutopayEnabled ? 'Update' : 'Save';
};

export type BillingPaymentMethodsProps = {
  accountNumber: string;
  onSubmit: (selectedCreditCardId: string | null | undefined, autopayEnabled?: boolean) => Promise<unknown>;
  allowRemove?: boolean;
  isPaymentInProgress?: boolean;
  submitDisabled?: boolean;
  autoPayDisabled?: boolean;
  infoMessage?: string;
  isSinglePayment?: boolean;
  onSubmitDataEventId?: string;
};

const BillingPaymentMethods = ({
  accountNumber,
  onSubmit,
  submitDisabled,
  isPaymentInProgress = false,
  isSinglePayment = false,
  infoMessage,
  autoPayDisabled = false,
  allowRemove = false,
  onSubmitDataEventId,
}: BillingPaymentMethodsProps) => {
  const {
    deleteZuoraPaymentMethod,
    importZuoraPaymentMethods,
    getAccountPaymentMethods,
    setPaymentMethodLock,
    paymentMethodState,
  } = useAccountPaymentMethods();
  const {
    loadingState: accountPaymentMethodsLoadingState,
    accountPaymentMethods,
    paymentMethodLock,
    isAutopayEnabled,
    autopayMethodId,
  } = paymentMethodState;
  const isEditPaymentMethodsPage = !isSinglePayment;
  const showAutopay = isEditPaymentMethodsPage;
  const submitLabel = makeLabel(isSinglePayment, isAutopayEnabled);
  const { user } = useRentalManagerUser();
  const { impersonatorEmail, impersonatorUserToken } = user;
  const { isProduction } = useNodeEnv();
  const disableImpersonatorPayments = isProduction && !!(impersonatorEmail || impersonatorUserToken);
  const { isMaintenanceMode } = useAppMaintenance();

  const [autoPay, setAutoPay] = React.useState(isAutopayEnabled);
  const [autoPayConditionsAccepted, setAutoPayConditionsAccepted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [selectedCreditCardId, setSelectedCreditCardId] = React.useState(autopayMethodId);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showBillingAddPaymentModal, setShowBillingAddPaymentModal] = React.useState(false);
  const [confirmationModalData, setConfirmationModalData] = React.useState<ConfirmationModalProps | null>(null);
  const [overridePaymentMethods, setOverridePaymentMethods] = React.useState(false);

  const selectedPaymentMethod = accountPaymentMethods.find((method) => method.paymentMethodId === selectedCreditCardId);
  const isAchCurrentlySelected = selectedPaymentMethod && selectedPaymentMethod.paymentType === 'ACH';

  React.useEffect(() => {
    const userContainsPaymentMethod = accountPaymentMethods.some(
      (paymentMethod) => paymentMethod.paymentMethodId === autopayMethodId,
    );
    setSelectedCreditCardId(userContainsPaymentMethod ? autopayMethodId : null);
    setAutoPay(isAutopayEnabled);
  }, [accountPaymentMethods]);

  React.useEffect(() => {
    if (autoPayDisabled) {
      setAutoPay(false);
      setAutoPayConditionsAccepted(false);
    }
  }, [autoPayDisabled]);

  const handleGetPaymentMethods = () => {
    getAccountPaymentMethods(accountNumber);
  };

  // @ts-ignore
  const handleWebsocketMessge = (message) => {
    let data;
    try {
      data = JSON.parse(message.data);
    } catch (error) {
      return;
    }
    if (data.name !== NOTIFICATION_TYPE.BILLING_PORTAL_PAYMENT_METHODS_REFRESH) {
      return;
    }
    const dtoData = JSON.parse(data.payload);
    if (dtoData.accountNumber === accountNumber) {
      setPaymentMethodLock(false);
      handleGetPaymentMethods();
    }
  };

  const handleCreditCardAdded = () => {
    setShowBillingAddPaymentModal(false);
    setPaymentMethodLock(true);
    importZuoraPaymentMethods(accountNumber);
  };

  React.useEffect(() => {
    handleGetPaymentMethods();
    setOverridePaymentMethods(Cookies.get('ZG_OVERRIDE_PAYMENT_METHOD') === 'true');
    window.rentalManagerApiWebSocket.addEventListener('message', handleWebsocketMessge);
    return () => window.rentalManagerApiWebSocket.removeEventListener('message', handleWebsocketMessge);
  }, []);

  const dataEventId = React.useMemo(() => {
    if (onSubmitDataEventId) return onSubmitDataEventId;

    if (!isSinglePayment) {
      return EVENT_IDS.ADD_AUTOPAY; // enable autopay
    }

    return EVENT_IDS.OTHER_AMOUNT; // single payment
  }, [onSubmitDataEventId, isSinglePayment]);

  const canSubmit = () => {
    if (submitDisabled) return false;

    // If in edit card mode
    if (isEditPaymentMethodsPage) {
      // no changes
      if ((isAutopayEnabled === autoPay && autopayMethodId) === selectedCreditCardId && selectedCreditCardId !== null) {
        return false;
      }
      // Autopay is on but user changed the card
      if (isAutopayEnabled === autoPay && autopayMethodId !== selectedCreditCardId && selectedCreditCardId !== null) {
        return autoPayConditionsAccepted;
      }
      if (isAutopayEnabled && !autoPay) {
        return true; // User turned off autopay
      }
      if (!isAutopayEnabled && autoPay && autopayMethodId === selectedCreditCardId) {
        // User toggles on autopay, and default is already selected
        return autoPayConditionsAccepted;
      }
      if (!isAutopayEnabled && autoPay) {
        return autopayMethodId !== selectedCreditCardId && selectedCreditCardId !== null && autoPayConditionsAccepted;
      }
    }

    // Single Payment Mode
    if (!selectedCreditCardId) {
      // No card selected at all
      return false;
    }
    if (isAchCurrentlySelected) {
      // Confirmation necessary for ACH
      return autoPayConditionsAccepted;
    }
    return true;
  };

  const submit = () => {
    setIsSubmitting(true);
    setConfirmationModalData(null);
    setPaymentMethodLock(true);
    onSubmit(selectedCreditCardId, autoPayConditionsAccepted)
      .then(() => {
        setErrorMessage(null);
        setIsSubmitting(false);
        return;
      })
      .catch(() => {
        setErrorMessage('Something went wrong, please try again!');
        setIsSubmitting(false);
      });
  };

  const handleSubmit = () => {
    // only display modal if its the edit payment methods page AND user disabled autopay
    if (!isSinglePayment && isAutopayEnabled === true && !autoPay) {
      setConfirmationModalData({
        header: 'Are you sure you want to cancel auto pay?',
        submitButtonTitle: 'Keep Auto Pay',
        cancelButtonTitle: 'Cancel Auto Pay',
        textContent:
          'Auto pay lets you avoid the risk of missed payments. ' +
          'If you cancel, you’ll have to pay manually each month.',
        onCancel: submit,
        onSubmit: () => setConfirmationModalData(null),
      });
      return;
    }
    submit();
  };

  const handleRemoveCreditCardClick = (creditCard: UserZuoraPaymentMethodDto) => {
    const isAchSelected = creditCard.paymentType === 'ACH';
    const endingNumbers = creditCard.maskNumber.slice(creditCard.maskNumber.length - 4, creditCard.maskNumber.length);
    setConfirmationModalData({
      header: `Are you sure you want to remove
                ${isAchSelected ? 'bank account' : 'card'} ${endingNumbers}?`,
      submitButtonTitle: 'Yes, remove it',
      textContent: `Removing this payment method
                will not cancel any of your ongoing payments that use this
                ${isAchSelected ? 'bank account' : 'card'}.`,
      onSubmit: () => {
        setIsSubmitting(true);
        emitRemovePaymentMethodEvent({ user });
        setPaymentMethodLock(true);
        setConfirmationModalData(null);
        deleteZuoraPaymentMethod(accountNumber, creditCard.paymentMethodId)
          .then(() => {
            setErrorMessage(null);
            setIsSubmitting(false);
            return;
          })
          .catch((err) => {
            setErrorMessage(err.message);
            setIsSubmitting(false);
            setPaymentMethodLock(false);
          });
      },
    });
  };

  const handleAddCreditCardClick = () => {
    setShowBillingAddPaymentModal(true);
  };

  const showConfirmation = () => {
    if (isSinglePayment) {
      // On single payment page only show confirmation checkbox if is ACH
      return isAchCurrentlySelected;
    }
    if (!showAutopay) {
      return false;
    }
    // edit payment methods
    if (autoPay) {
      return !isAutopayEnabled || autopayMethodId !== selectedCreditCardId;
    }
    return false;
  };

  const isLoading =
    accountPaymentMethodsLoadingState === LoadingConstants.LOADING ||
    accountPaymentMethodsLoadingState === LoadingConstants.INITIAL;
  const errorLoadingPaymentMethods = accountPaymentMethodsLoadingState === LoadingConstants.ERROR;

  const disableControls = (): boolean => {
    if (overridePaymentMethods) {
      return false;
    }
    return (
      isLoading ||
      errorLoadingPaymentMethods ||
      paymentMethodLock ||
      isPaymentInProgress ||
      disableImpersonatorPayments ||
      isMaintenanceMode
    );
  };

  if (errorLoadingPaymentMethods) {
    return <Alert tone="critical">Error loading payment methods, please try again later.</Alert>;
  }

  const filteredPaymentMethods = accountPaymentMethods;

  const formatPaymentMethod = (paymentMethod: UserZuoraPaymentMethodDto) => {
    if (paymentMethod.paymentType === 'CreditCard') {
      return `${paymentMethod.brand} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
    }
    if (paymentMethod.paymentType === 'ACH') {
      return `${paymentMethod.bankName} ending with 
            ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
    }
    return 'Unknown Payment Method';
  };

  return (
    <React.Fragment>
      <FormField>
        <Label required={true}>
          <Text textStyle="body-bold">Payment method:</Text>
        </Label>
        {isLoading && <SpinnerContainer />}
        {paymentMethodLock && !isPaymentInProgress && (
          <StyledAlertText>
            <Text css={{ paddingRight: 'sm' }}>Please wait while we update your payment methods.</Text>
            <Spinner size="sm" />
          </StyledAlertText>
        )}
        {filteredPaymentMethods.map((paymentMethod) => (
          <Box
            key={paymentMethod.paymentMethodId}
            css={{ display: 'flex', alignItems: 'flex-start', gap: 'sm', marginBottom: 'sm' }}
          >
            <Radio
              name={`payment-method-${paymentMethod.paymentMethodId}`}
              checked={selectedCreditCardId === paymentMethod.paymentMethodId}
              onChange={() => {
                setSelectedCreditCardId(paymentMethod.paymentMethodId);
                setAutoPayConditionsAccepted(false);
              }}
              disabled={disableControls()}
            />
            <Box css={{ flexGrow: 1 }}>
              <Label>
                {formatPaymentMethod(paymentMethod)}
                {allowRemove &&
                  !paymentMethod.defaultPaymentMethod &&
                  !paymentMethodLock &&
                  !isPaymentInProgress &&
                  !disableImpersonatorPayments && (
                    <TextButton
                      onClick={() => handleRemoveCreditCardClick(paymentMethod)}
                      disabled={disableControls()}
                      data-eventid={EVENT_IDS.REMOVE_PAYMENT_METHOD}
                      css={{ marginLeft: 'sm' }}
                    >
                      Remove
                    </TextButton>
                  )}
                {autopayMethodId === paymentMethod.paymentMethodId && isAutopayEnabled && (
                  <Text textStyle="body-sm" css={{ marginLeft: 'sm' }}>
                    (Default)
                  </Text>
                )}
                {paymentMethod.paymentType === 'CreditCard' && (
                  <Box css={{ marginTop: 'xs' }}>
                    <Text textStyle="body-sm" css={{ color: 'gray.600' }}>
                      {`Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`}
                    </Text>
                  </Box>
                )}
              </Label>
            </Box>
          </Box>
        ))}
      </FormField>
      <Box css={{ marginBlock: 'sm' }}>
        <Button
          tone="brand"
          css={{ width: '100%' }}
          onClick={() => handleAddCreditCardClick()}
          disabled={disableControls()}
        >
          <Icon size="sm">
            <IconPlusFilled />
          </Icon>{' '}
          Add payment method
        </Button>
      </Box>
      {showAutopay && !isAutopayEnabled && (
        <FormField>
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'sm' }}>
            <Checkbox
              name="autopay-checkbox"
              checked={autoPay}
              onChange={() => {
                setAutoPay(!autoPay);
                setAutoPayConditionsAccepted(false);
              }}
              disabled={autoPayDisabled || disableControls()}
            />
            <Label>Auto Pay</Label>
          </Box>
        </FormField>
      )}
      {showConfirmation() && (
        <Box css={{ marginBlock: 'sm' }}>
          <Box css={{ marginBottom: 'xs', marginTop: 'lg' }}>
            <Text textStyle="body-sm" css={{ fontWeight: 'bold' }}>
              Terms & Conditions
            </Text>
          </Box>
          <TermsAndConditionsWidget
            accountPaymentMethods={accountPaymentMethods}
            selectedCreditCardId={selectedCreditCardId}
            isSinglePayment={isSinglePayment}
          />
          <FormField css={{ marginBlock: 'sm' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: 'sm' }}>
              <Checkbox
                name="terms-conditions-checkbox"
                checked={autoPayConditionsAccepted}
                onChange={() => setAutoPayConditionsAccepted(!autoPayConditionsAccepted)}
                disabled={disableControls()}
              />
              <Label>Yes, I accept the Terms & Conditions</Label>
            </Box>
          </FormField>
        </Box>
      )}
      {infoMessage && (
        <Box css={{ margin: 'sm' }}>
          <Paragraph textStyle="body-sm">*{infoMessage}</Paragraph>
        </Box>
      )}
      {showAutopay && autoPayDisabled && (
        <Box css={{ margin: 'sm' }}>
          <Paragraph textStyle="body-sm">*Auto pay is only available if you pay the full balance.</Paragraph>
        </Box>
      )}
      {errorMessage && <BillingError errorMsg={errorMessage} />}
      {isPaymentInProgress && (
        <StyledAlertText>
          <Text css={{ paddingRight: 'sm' }}>Payment in progress.</Text>
          <Spinner size="sm" />
        </StyledAlertText>
      )}
      <Box css={{ marginTop: 'md' }}>
        <Button
          tone="brand"
          emphasis="filled"
          disabled={!canSubmit() || disableControls() || isSubmitting}
          onClick={handleSubmit}
          id="BillingPaymentMethods-submit"
          css={{ width: '100%' }}
          data-eventid={dataEventId}
        >
          {submitLabel}
        </Button>
      </Box>
      {disableImpersonatorPayments && (
        <Paragraph textStyle="body-sm" css={{ fontWeight: 'semibold' }}>
          Editing disabled while impersonating
        </Paragraph>
      )}
      {showBillingAddPaymentModal && (
        <BillingAddPaymentModal
          accountNumber={accountNumber}
          onCreditCardAdded={handleCreditCardAdded}
          onClose={() => setShowBillingAddPaymentModal(false)}
        />
      )}
      {confirmationModalData && (
        <ConfirmationModal
          submitButtonTitle={confirmationModalData.submitButtonTitle}
          cancelButtonTitle={confirmationModalData.cancelButtonTitle}
          textContent={confirmationModalData.textContent}
          onSubmit={confirmationModalData.onSubmit}
          onCancel={confirmationModalData.onCancel}
          header={confirmationModalData.header}
          onClose={() => setConfirmationModalData(null)}
        />
      )}
    </React.Fragment>
  );
};

export default BillingPaymentMethods;
